"use client"

import { useState, useMemo } from "react"
import { UseFormReturn, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { FormSectionCard } from "./FormSectionCard"
import { Button } from "@/components/ui/button"
import { CurrencyInput } from "@/components/ui/currency-input"
import { DecimalInput } from "@/components/ui/decimal-input"
import { DebouncedInput } from "@/components/ui/debounced-input"
import { Input } from "@/components/ui/input"

import { Form, FormControl, FormErrorsVisible, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  PlusCircle,
  UserPlus,
  Users,
  ExternalLink,
  Trash2,
} from "lucide-react"
import { VirtualizedSelect } from "@/components/ui/virtualized-select"
import { DatePicker } from "@/components/ui/date-picker"
import { useLanguage } from "@/contexts/language-context"
import { useFormatters } from "@/hooks/use-formatters"
import { useFormSectionNavigation } from "@/hooks/use-form-section-navigation"
import { useParentNavigation } from "@/hooks/use-parent-navigation"
import { useWorkerProfiles, isSelectableWorkerProfile, type WorkerProfile } from "@/hooks/use-worker-profiles"
import type { WorkerProfileSummary } from "@/types/api/engagement"
import { useCodeValues } from "@/hooks/use-code-values"
import { usePeopleApi } from "@/hooks/use-people-api"
import { salariedPayShapeFromDetails, hourlyPayShapeFromDetails, profileRatesFor, type TaskCoverage } from "@/lib/utils/worker-pay-shape"
import { allocateRateSlots, isBaseRateType, payRateRows, selectedProfileRates, unselectedProfileRates } from "@/lib/utils/worker-rate-slots"
import { CODE_TABLE_CATEGORIES } from "@/lib/constants/code-table-categories"
import type { ProfilePayRate } from "@/types/api/people"
import { type WorkerFormData as Worker, type WorkerFormArrayData, type WorkerType, WORKER_TYPE, createWorkerFormSchema } from "@/lib/schemas"
import { ENGAGEMENT_BILLING_TYPE, ENGAGEMENT_TYPE } from "@/lib/constants/engagement-codes"
import { PROFILE_WORKER_T4_TYPE } from "@/lib/constants/profile-codes"
import { isWorkerNew, isWorkerExisting } from "@/lib/utils/worker-utils"
import { laterOf, parseDateOnly } from "@/lib/utils"
import { formatDateOnly } from "@/lib/adapters/engagement-adapter"
import { useWorkerEndDateConflict } from "@/hooks/use-worker-end-date-conflict"
import { useWorkerStartDateConflict } from "@/hooks/use-worker-start-date-conflict"
import { standingRefusals, type WorkerErrorContext, type WorkerErrorSlot, type WorkerRefusals } from "@/lib/utils/worker-save-errors"
import { billRateWarning } from "@/lib/utils/bill-rate-warning"
import WorkerCard from "./WorkerCard"
import { WorkerRemovalDateDialog } from "./WorkerRemovalDateDialog"
import { ScheduleBudgetedHoursDialog } from "./ScheduleBudgetedHoursDialog"
import { ScheduledHoursConfirmDialog, type ScheduledHoursConfirmation } from "./ScheduledHoursConfirmDialog"
import { ScheduledHoursSummary } from "./ScheduledHoursSummary"
import {
  effectiveLastDay,
  lastDayOfWorkEffects,
  pendingEntries,
  toDateOnly,
  todayDateOnly,
} from "@/lib/utils/scheduled-budgeted-hours"
import React from "react"
import { RATE_UNIT } from '@/lib/constants/work-order-codes'
import { ScrollArea } from '@radix-ui/react-scroll-area'

const CURRENCY_OVERRIDE_NONE = "__USE_ENGAGEMENT_CURRENCY__"

// Helper function to determine worker type from profile
const getWorkerTypeFromProfile = (profile: WorkerProfile): WorkerType => {
  return (profile.T4TypeId === PROFILE_WORKER_T4_TYPE.T4_SALARIED) 
    ? WORKER_TYPE.T4_SALARIED 
    : WORKER_TYPE.REGULAR
}

interface WorkersSectionProps {
  form: UseFormReturn<WorkerFormArrayData>
  onWorkerAdd: (worker: Worker) => void
  onWorkerEdit: (worker: Worker) => void
  onWorkerRemove: (workerUserProfileId: number) => void
  isCompleted: boolean
  isReadonly?: boolean
  billingType: string
  engagementType: string
  engagementStatus?: string
  engagementRecordId?: number | null
  previousWorkerIds?: number[] | null
  engagementStartDate?: Date | null
  engagementEndDate?: Date | null
  currency?: string
  hiddenSections: Record<string, boolean>
  canViewPayRates: boolean
  workerProfiles: Record<number, WorkerProfileSummary>
  /**
   * What the engagement's active tasks need a worker to be paid at. Decides which of a profile's rates a
   * worker starts on, since hours land on a rate whose billability matches their task's.
   */
  taskCoverage?: TaskCoverage
  /**
   * What a refused save said about each worker, by profile id. Marks their card and shows each reason
   * under the field it is about, until something it was refused over changes.
   */
  workerSaveErrors?: Record<number, WorkerRefusals>
}

// Default worker template
const DEFAULT_WORKER: Worker = {
  workerUserProfileId: 0,
  payRates: [],
  jobCategoryCode: "",
  jobFunctionCode: "",
  jobTitle: "",
  lineOfBusinessCode: null,
  budgetedHours: null,
  scheduledBudgetedHours: [],
  reportingBillRate: null,
  billRate: null,
  billRateUnit: RATE_UNIT.HOUR,
  payRate: null,
  payRateUnit: RATE_UNIT.HOUR,
  scheduledPayRate: null,
  scheduledPayRateEffectiveDate: null,
  scheduledReportingPayRate: null,
  scheduledReportingPayRateEffectiveDate: null,
  reportingPayRate: null,
  paySchedule: "",
  paymentCurrencyCode: null,
  legacySyncStatus: undefined, // Default legacy sync status for new workers
  isRemoved: false, // Default to active (not removed)
  isNew: false, // Will be set based on context
  firstDayOfWork: null, // Will be set for new workers in change requests
  lastDayOfWork: null, // Will be set for removed workers
  terminationTypeId: null, // Set only when a removal was an actual termination
  terminationReasonIds: null, // Saved termination reason(s); gates the Terminated vs Removed badge
}

export const WorkersSection = ({
  form,
  onWorkerAdd,
  onWorkerEdit,
  onWorkerRemove,
  isCompleted,
  isReadonly = false,
  billingType,
  engagementType,
  engagementStatus,
  engagementRecordId,
  previousWorkerIds,
  engagementStartDate,
  engagementEndDate,
  currency,
  hiddenSections,
  canViewPayRates,
  workerProfiles,
  taskCoverage,
  workerSaveErrors,
}: WorkersSectionProps) => {
  const { dictionary } = useLanguage()
  const { formatDate } = useFormatters()
  const navigationProps = useFormSectionNavigation('workers', hiddenSections)
  const { getCodeValues: getByCategory, getCodeValueText, getRelatedCodeValuesByCode, getCodeValueTextByCode } = useCodeValues()
  const { fetchT4SalariedDetails, fetchEngagementDetails } = usePeopleApi()
  const { navigate: navigateToParent } = useParentNavigation()

  const workers = form.watch('workers')

  // Get worker user profiles (global, not tied to organizations)
  const { 
    workerProfiles: workerUserProfiles,
    loading: loadingWorkerProfiles 
  } = useWorkerProfiles()

  // Check if all available workers are already selected
  const allWorkersSelected = !loadingWorkerProfiles && 
    workerUserProfiles.length > 0 && 
    workerUserProfiles.every(profile => workers.some(worker => worker.workerUserProfileId === profile.Id))

  // Local state for worker dialog (handles both add and edit)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)

  // GW-154: pre-flight check when shortening a worker's Last Day of Work
  const { checkEndDateConflict } = useWorkerEndDateConflict()
  // GW-154: pre-flight check when pushing a worker's First Day of Work later
  const { checkStartDateConflict } = useWorkerStartDateConflict()
  const [isCheckingEndDate, setIsCheckingEndDate] = useState(false)
  
  // Local state for date dialog (handles remove with dates)
  const [isDateDialogOpen, setIsDateDialogOpen] = useState(false)
  const [pendingWorkerRemoval, setPendingWorkerRemoval] = useState<Worker | null>(null)

  // Scheduled budgeted hours: the change list editor, and the confirmations shown when removing a worker
  // or moving their Last Day of Work would affect pending changes.
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false)
  const [removalConfirmation, setRemovalConfirmation] = useState<ScheduledHoursConfirmation | null>(null)
  const [lastDayConfirmation, setLastDayConfirmation] = useState<ScheduledHoursConfirmation | null>(null)
  
  // Memoize the worker form resolver to prevent unnecessary recreation. canViewPayRates skips the
  // redacted/hidden Pay Rate requirements so a non-viewer's dialog isn't flagged as incomplete.
  const workerFormResolver = useMemo(() => {
    return zodResolver(createWorkerFormSchema(billingType, engagementType, engagementStartDate || undefined, engagementEndDate || undefined, canViewPayRates, taskCoverage))
  }, [billingType, engagementType, engagementStartDate, engagementEndDate, canViewPayRates, taskCoverage])

  // Form for dialog worker with conditional validation
  const dialogForm = useForm<Worker>({
    resolver: workerFormResolver,
    mode: 'onChange',
    defaultValues: DEFAULT_WORKER as Worker
  })

  const dialogWorker = dialogForm.watch()

  // Where the worker dialog's scheduled hours changes may fall, read off the dialog's own dates so moving a
  // date there moves the bounds with it.
  const dialogScheduleBounds = {
    today: todayDateOnly(),
    firstDay: dialogWorker.firstDayOfWork
      ? toDateOnly(dialogWorker.firstDayOfWork)
      : engagementStartDate ? toDateOnly(engagementStartDate) : null,
    lastDay: effectiveLastDay(dialogWorker.lastDayOfWork, engagementEndDate),
  }

  // The schema reports scheduled change problems on each entry and on the list as a whole; the dialog
  // shows them together under the field, since the entries are not individually editable there.
  const dialogScheduleErrors = (() => {
    const messages = new Set<string>()
    // `ref` is the bound element, not an error, and walking it would recurse into the DOM.
    const collect = (node: unknown) => {
      if (!node || typeof node !== 'object') return
      for (const [key, value] of Object.entries(node)) {
        if (key === 'message' && typeof value === 'string' && value) messages.add(value)
        else if (key !== 'ref') collect(value)
      }
    }
    collect(dialogForm.formState.errors.scheduledBudgetedHours)
    return [...messages]
  })()
  // The flat fields stand in for a worker stored before the rate list existed, so an unmigrated worker's
  // dialog reads the same as it always did rather than empty.
  const dialogPayRates = payRateRows(dialogWorker)

  // The rate rows are rendered outside FormField, so there is no FormMessage to carry the schema's
  // per-rate message. Without this the dialog refuses to submit and says nothing about why.
  const payRateErrorFor = (index: number): string | undefined =>
    dialogForm.formState.errors.payRates?.[index]?.payRate?.message

  // An issue the schema raised against the rate list itself rather than against one of its rates. React
  // Hook Form puts it on `root` where the field is an array, and on the field where it is not, so both
  // are read.
  const payRateListError =
    dialogForm.formState.errors.payRates?.root?.message ?? dialogForm.formState.errors.payRates?.message

  // Which rates the worker's profile offers, so the dialog can offer the ones they are not on yet. Held
  // here because the add flow discards the profile response and the edit flow never fetched one.
  const [profilePayRates, setProfilePayRates] = useState<ProfilePayRate[]>([])

  // Whether anyone is billed for hours here at all. Milestone billing invoices against milestones and an
  // internal engagement invoices nobody, so neither carries a worker Bill Rate, and neither has anything
  // to say about whether a rate bills: that question belongs to the rate, on the profile, and asking it
  // of a placement that bills nothing has no answer worth showing.
  const billsForHours =
    billingType !== ENGAGEMENT_BILLING_TYPE.MILESTONE && engagementType !== ENGAGEMENT_TYPE.INTERNAL

  // What outside a worker a refused save was refused over. Built from the same values the page records
  // a refusal against, so a refusal stands exactly until one of them changes.
  const refusalContext = useMemo<WorkerErrorContext>(
    () => ({ engagementStartDate, engagementEndDate, taskCoverage }),
    [engagementStartDate, engagementEndDate, taskCoverage]
  )

  // What a refused save said about the worker open in the dialog, read against the dialog's values so a
  // refusal stops showing as soon as the field it is about is changed. Each sits under its field, unless
  // the schema already flags that field. One about a field the dialog is not showing leads the dialog.
  const dialogSaveErrors = isEditMode
    ? standingRefusals(workerSaveErrors?.[dialogWorker.workerUserProfileId], dialogWorker, refusalContext)
    : {}
  const dialogBillRateWarning = billRateWarning(dialogWorker, { billingType, engagementType, currency, canViewPayRates })
  const dialogShowsSlot: Record<Exclude<WorkerErrorSlot, 'general'>, boolean> = {
    firstDayOfWork: true,
    lastDayOfWork: true,
    payRates: dialogWorker.type === WORKER_TYPE.REGULAR && canViewPayRates,
  }
  const dialogSaveErrorUnder = (slot: Exclude<WorkerErrorSlot, 'general'>, isFlaggedLive: boolean) =>
    dialogShowsSlot[slot] && !isFlaggedLive ? dialogSaveErrors[slot] : undefined
  const dialogHeadSaveErrors = (Object.entries(dialogSaveErrors) as [WorkerErrorSlot, string][])
    .filter(([slot]) => slot === 'general' || !dialogShowsSlot[slot])
    .map(([, message]) => message)

  // What a worker added or restored here starts on. Where nobody is billed for hours every rate reaches the
  // work order non-billable, so Primary alone covers every task and nothing else is asked for.
  const seedCoverage = billsForHours ? taskCoverage : undefined

  // Every rate the worker is not on yet. All of them are offered: where nobody is billed for hours every
  // rate reaches the work order non-billable, so any of them can be recorded against a task here.
  const availablePayRates = unselectedProfileRates(dialogPayRates, profilePayRates)

  /**
   * How a rate reads wherever it is named: its type, with any wording the profile put beside it in
   * brackets after. Shared by the list of rates a worker holds and the picker offering the rest, so the
   * rate someone chooses is the one they see appear.
   */
  const payRateLabel = (rateTypeCode?: string | null, description?: string | null): string => {
    const type = getCodeValueTextByCode(rateTypeCode || '', CODE_TABLE_CATEGORIES.RATE_TYPE)
      || rateTypeCode
      || dictionary?.engagement?.workers?.rateName
      || "Rate Name"
    const wording = description?.trim()

    return wording ? `${type} (${wording})` : type
  }

  /**
   * Applies a change of selection, keeping the flat pay fields on the first rate. Primary cannot be
   * deselected and goes first when added, so once it is held the mirrors never move.
   */
  const applyRateSelection = (selection: ProfilePayRate[]) => {
    const payRates = allocateRateSlots(selection)

    dialogForm.setValue('payRates', payRates, { shouldValidate: true, shouldDirty: true })
    dialogForm.setValue('payRate', payRates[0]?.payRate ?? null)
    dialogForm.setValue('payRateUnit', payRates[0]?.payRateUnitCode ?? RATE_UNIT.HOUR)
  }

  const addPayRate = (profileRateId: string) => {
    const added = availablePayRates.find(rate => rate.Id === profileRateId)
    if (!added) {
      return
    }

    const held = selectedProfileRates(dialogPayRates, profilePayRates)

    // Primary is the base rate, so a placement that was missing it takes it in front.
    applyRateSelection(isBaseRateType(added.RateTypeCode) ? [added, ...held] : [...held, added])
  }

  const removePayRate = (index: number) => {
    const removed = dialogPayRates[index]
    if (!removed || isBaseRateType(removed.rateTypeCode)) {
      return
    }

    applyRateSelection(
      selectedProfileRates(dialogPayRates, profilePayRates)
        .filter(rate => rate.Id !== removed.profileRateId),
    )
  }

  // The persisted version of the worker currently being edited (if any). Used to detect in-dialog
  // changes to pay-affecting fields — specifically Pay Schedule (GW-237), which is now editable per
  // engagement instead of mirrored from the profile.
  const editingWorkerOriginal = isEditMode
    ? workers.find(w => w.workerUserProfileId === dialogWorker.workerUserProfileId)
    : undefined

  // Apply a profile-derived pay shape (from the shared mappers) onto the dialog form. Billability comes
  // with each rate off the profile, so nothing here adjusts it.
  const applyPayShapeToDialog = (shape: Partial<Worker>) => {
    (Object.keys(shape) as (keyof Worker)[]).forEach((key) => {
      dialogForm.setValue(key, shape[key] as never)
    })
  }

  // Determine if the current dialog worker is new (doesn't exist in previous version)
  const isNewWorker = dialogWorker.workerUserProfileId ? 
    isWorkerNew(dialogWorker.workerUserProfileId, previousWorkerIds) : false

  // Helper function to get profile type text from code tables
  const getProfileTypeText = (profile: WorkerProfile): string => {
    if (profile.T4TypeId) {
      // T4 worker - use T4TypeId with 'usr.CodeProfileT4Type'
      return getCodeValueText(profile.T4TypeId, "usr.CodeProfileWorkerT4Type") || "T4"
    } else {
      // Other worker types - use ProfileTypeId with 'usr.CodeProfileType'
      return getCodeValueText(profile.ProfileTypeId, "usr.CodeProfileType") || "Unknown"
    }
  }

  // Resolve the worker's Line of Business code (enriched onto the profile) to display text.
  // Returns null when the worker has no LOB so the dropdown label can omit the bracket.
  const getLineOfBusinessText = (profile: WorkerProfile): string | null => {
    if (!profile.LineOfBusinessCode) return null
    const lob = getByCategory('org.CodeLineOfBusiness').find(l => l.code === profile.LineOfBusinessCode)
    return lob?.description || lob?.text || profile.LineOfBusinessCode
  }

  // Helper function to filter available workers (not already selected, unless in edit mode for current worker)
  const isWorkerAvailable = (profile: WorkerProfile, editMode: boolean, editingWorkerProfileId?: number): boolean => {
    return !workers.some(worker => worker.workerUserProfileId === profile.Id) || 
           (editMode && profile.Id === editingWorkerProfileId)
  }

  // Memoize the worker options for VirtualizedSelect
  const workerOptions = useMemo(() => {
    if (!workerUserProfiles) return []
    
    return workerUserProfiles
      .filter(profile => {
        // Must not be already selected (unless editing)
        if (!isWorkerAvailable(profile, isEditMode, dialogWorker.workerUserProfileId)) {
          return false
        }

        // useWorkerProfiles widened its filter to include Inactive / PendingDeactivation so that
        // RemovedWorker entries resolve for display. Shared with the hook, which enriches exactly
        // this set with Line of Business, so widening here alone would drop the LOB bracket.
        return isSelectableWorkerProfile(profile)
      })
      .map((profile) => ({
        profile,
        workerType: getWorkerTypeFromProfile(profile)
      }))
  }, [workerUserProfiles, workers, isEditMode, dialogWorker.workerUserProfileId])

  const openAddDialog = () => {
    dialogForm.reset(DEFAULT_WORKER)
    setProfilePayRates([])
    // Default the Engagement Start Date to the engagement's actual start date (GW-97).
    if (engagementStartDate) {
      dialogForm.setValue('firstDayOfWork', engagementStartDate)
    }
    dialogForm.trigger()

    setIsEditMode(false)
    setIsDialogOpen(true)
  }

  const openEditDialog = (worker: Worker) => {
    // GW-334: a copied worker can arrive with no Engagement Start Date, when the copy ran before the
    // engagement's own Start Date was set. Default a blank one to the engagement start date the way
    // openAddDialog does (GW-97). A worker that already has a date keeps it.
    const workerWithStartDefault = {
      ...worker,
      firstDayOfWork: worker.firstDayOfWork ?? engagementStartDate ?? null,
    }

    // Set the selected worker value for display in edit mode
    const profile = workerUserProfiles.find(p => p.Id === worker.workerUserProfileId)
    if (profile) {
      // Calculate current worker type based on profile's T4TypeId (could have changed)
      const currentWorkerType = getWorkerTypeFromProfile(profile)
      
      // Update the dialog worker with current type and ensure rate units have defaults
      dialogForm.reset({
        ...workerWithStartDefault,
        type: currentWorkerType,
        jobCategoryCode: worker.jobCategoryCode || DEFAULT_WORKER.jobCategoryCode,
        jobFunctionCode: worker.jobFunctionCode || DEFAULT_WORKER.jobFunctionCode,
        jobTitle: worker.jobTitle || DEFAULT_WORKER.jobTitle,
        billRateUnit: worker.billRateUnit || DEFAULT_WORKER.billRateUnit,
        payRateUnit: worker.payRateUnit || DEFAULT_WORKER.payRateUnit,
        scheduledPayRate: worker.scheduledPayRate ?? null,
        scheduledPayRateEffectiveDate: worker.scheduledPayRateEffectiveDate ?? null,
        scheduledReportingPayRate: worker.scheduledReportingPayRate && worker.scheduledReportingPayRate > 0 ? worker.scheduledReportingPayRate : null,
        scheduledReportingPayRateEffectiveDate: worker.scheduledReportingPayRateEffectiveDate ?? null
      })
    } else {
      // Fallback if profile not found - still ensure rate units have defaults
      dialogForm.reset({
        ...workerWithStartDefault,
        jobCategoryCode: worker.jobCategoryCode || DEFAULT_WORKER.jobCategoryCode,
        jobFunctionCode: worker.jobFunctionCode || DEFAULT_WORKER.jobFunctionCode,
        jobTitle: worker.jobTitle || DEFAULT_WORKER.jobTitle,
        billRateUnit: worker.billRateUnit || DEFAULT_WORKER.billRateUnit,
        payRateUnit: worker.payRateUnit || DEFAULT_WORKER.payRateUnit,
        scheduledPayRate: worker.scheduledPayRate ?? null,
        scheduledPayRateEffectiveDate: worker.scheduledPayRateEffectiveDate ?? null,
        scheduledReportingPayRate: worker.scheduledReportingPayRate && worker.scheduledReportingPayRate > 0 ? worker.scheduledReportingPayRate : null,
        scheduledReportingPayRateEffectiveDate: worker.scheduledReportingPayRateEffectiveDate ?? null
      })
    }

    dialogForm.trigger()
    setIsEditMode(true)
    setIsDialogOpen(true)

    // The edit path never needed the profile before, because every pay field it shows was already on the
    // worker. The picker needs the rates the worker is NOT on, which only the profile knows, so fetch it
    // after the dialog is up rather than holding the dialog on a request.
    setProfilePayRates([])
    if (getWorkerTypeFromProfile(profile ?? ({} as WorkerProfile)) === WORKER_TYPE.REGULAR) {
      fetchEngagementDetails(worker.workerUserProfileId)
        .then(details => {
          // Reopening the dialog on another worker while this is in flight would otherwise offer them the
          // first worker's rates, and selecting one would copy that profile's id and amount onto them.
          if (dialogForm.getValues('workerUserProfileId') !== worker.workerUserProfileId) {
            return
          }

          setProfilePayRates(profileRatesFor(details?.EngagementDetails))
        })
        .catch(error => {
          // The picker simply offers nothing. Every rate the worker already holds still reads, because
          // those come from the worker rather than the profile.
          console.error('Failed to fetch profile pay rates:', error)
        })
    }
  }

  const closeDialog = () => {
    setIsDialogOpen(false)
    dialogForm.reset(DEFAULT_WORKER as Worker)
    setProfilePayRates([])
    setIsEditMode(false)
  }

  const handleWorkerSelection = async (profileId: number) => {
    // Set isNew flag based on whether we're in a change request and worker exists in previous version
    const workerIsNew = isWorkerNew(profileId, previousWorkerIds)
    dialogForm.setValue('isNew', workerIsNew)

    // Find the profile and get its current worker type
    const profile = workerUserProfiles.find(p => p.Id === profileId)
    if (profile) {
      const workerType = getWorkerTypeFromProfile(profile)

      // Set the worker type based on profile
      dialogForm.setValue('type', workerType)

      const isBillRateVisible = billingType !== ENGAGEMENT_BILLING_TYPE.MILESTONE && engagementType !== ENGAGEMENT_TYPE.INTERNAL

      // Fetch engagement details once for ALL worker types
      // Job-related fields are engagement-specific and stored in EngagementDetails for all worker types
      // An added worker carries no extension dates, even if the dialog last held a worker who did
      dialogForm.setValue('originalStartDate', null)
      dialogForm.setValue('extensionStartDate', null)
      dialogForm.setValue('extensionEndDate', null)

      // Baseline the Engagement Start Date to the engagement's start date; once the worker's own start
      // date is fetched below it is refined to the later of the two (see laterOf). This baseline also
      // covers the case where the worker detail fetch fails or returns no start date. GW-97.
      if (engagementStartDate) {
        dialogForm.setValue('firstDayOfWork', engagementStartDate)
      }

      let engagementDetailsData = null
      dialogForm.setValue('jobCategoryCode', '')
      dialogForm.setValue('jobFunctionCode', '')
      dialogForm.setValue('jobTitle', '')
      dialogForm.setValue('paymentCurrencyCode', null)
      try {
        const engagementDetails = await fetchEngagementDetails(profileId)

        if (dialogForm.getValues('workerUserProfileId') !== profileId) {
          return
        }

        if (engagementDetails?.EngagementDetails) {
          engagementDetailsData = engagementDetails.EngagementDetails

          // Default job category, job function, and job title from engagement details
          // These fields are engagement-specific and remain in EngagementDetails for all worker types
          if (engagementDetailsData.JobCategoryCode) {
            dialogForm.setValue('jobCategoryCode', engagementDetailsData.JobCategoryCode)
          }

          if (engagementDetailsData.JobFunctionCode) {
            dialogForm.setValue('jobFunctionCode', engagementDetailsData.JobFunctionCode)
          }

          if (engagementDetailsData.JobTitle) {
            dialogForm.setValue('jobTitle', engagementDetailsData.JobTitle)
          }

          dialogForm.setValue('paymentCurrencyCode', engagementDetailsData.CurrencyCode ?? null)
        }
      } catch (error) {
        console.error('Failed to fetch engagement details:', error)
        // Leave fields empty if API call fails - safe for both worker types
      }

      // Worker-type specific field population
      if (workerType === WORKER_TYPE.T4_SALARIED) {
        // Clear regular-worker scheduled fields to prevent stale values when switching from a regular worker
        dialogForm.setValue('scheduledPayRate', null)
        dialogForm.setValue('scheduledReportingPayRateEffectiveDate', null)

        // Clear T4-specific fields before fetch to prevent stale values if fetch fails
        dialogForm.setValue('budgetedHours', null)
        dialogForm.setValue('reportingPayRate', null)
        dialogForm.setValue('scheduledReportingPayRate', null)
        dialogForm.setValue('scheduledPayRateEffectiveDate', null)
        if (isBillRateVisible) {
          dialogForm.setValue('billRate', null)
        }

        // For T4 Salaried workers, fetch T4-specific fields from T4SalariedDetails
        try {
          const t4Details = await fetchT4SalariedDetails(profileId)

          if (dialogForm.getValues('workerUserProfileId') !== profileId) { 
            return
          }
          const salariedDetails = t4Details.T4SalariedDetails

          // Pay-model fields come from the shared profile mapper (parity with restoreWorker — GW-210)
          applyPayShapeToDialog(salariedPayShapeFromDetails(salariedDetails))

          if (isBillRateVisible) {
            const defaultBillRate = Math.ceil((salariedDetails.ReportingPayRate ?? 0) * 1.2)
            dialogForm.setValue('billRate', defaultBillRate)
          }

          // For T4 Salaried workers, line of business comes from T4SalariedDetails
          if (salariedDetails.LineOfBusinessCode) {
            dialogForm.setValue('lineOfBusinessCode', salariedDetails.LineOfBusinessCode)
          }

          // Default the Engagement Start Date to the later of the worker's employment start date and the
          // engagement's start date (GW-97). The user can still change it.
          const t4StartDefault = laterOf(parseDateOnly(salariedDetails.StartDate), engagementStartDate ?? null)
          if (t4StartDefault) {
            dialogForm.setValue('firstDayOfWork', t4StartDefault)
          }
        } catch (error) {
          console.error('Failed to fetch T4 salaried details:', error)
          // Leave fields empty if API call fails
        }
      } else if (workerType === WORKER_TYPE.REGULAR) {
        // For regular workers, populate pay-related fields from already-fetched EngagementDetails
        // Reset pay-related form values first
        dialogForm.setValue('budgetedHours', null)
        // Cleared before the new profile's rates land, so switching profiles cannot leave the previous
        // worker's rates behind when the fetch returns nothing.
        dialogForm.setValue('payRates', [])
        setProfilePayRates([])
        dialogForm.setValue('payRate', null)
        dialogForm.setValue('payRateUnit', DEFAULT_WORKER.payRateUnit)
        dialogForm.setValue('paySchedule', DEFAULT_WORKER.paySchedule)

        // Clear scheduled fields to prevent stale values when switching profiles
        // (re-set from engagementDetailsData below if applicable)
        dialogForm.setValue('scheduledReportingPayRate', null)
        dialogForm.setValue('scheduledReportingPayRateEffectiveDate', null)
        dialogForm.setValue('scheduledPayRate', null)
        dialogForm.setValue('scheduledPayRateEffectiveDate', null)

        // Use the engagement details we already fetched above
        if (engagementDetailsData) {
          // Pay-model fields come from the shared profile mapper (parity with restoreWorker — GW-210)
          applyPayShapeToDialog(hourlyPayShapeFromDetails(engagementDetailsData, seedCoverage))
          // The mapper starts the worker on what the tasks need; the rest are what the picker offers.
          setProfilePayRates(profileRatesFor(engagementDetailsData))

          // For regular workers, line of business comes from engagement details
          if (engagementDetailsData.LineOfBusinessCode) {
            dialogForm.setValue('lineOfBusinessCode', engagementDetailsData.LineOfBusinessCode)
          }

          // Default the Engagement Start Date to the later of the worker's start date and the
          // engagement's start date (GW-97). The user can still change it.
          const regularStartDefault = laterOf(parseDateOnly(engagementDetailsData.StartDate), engagementStartDate ?? null)
          if (regularStartDefault) {
            dialogForm.setValue('firstDayOfWork', regularStartDefault)
          }
        }
      }
    }

    dialogForm.trigger()
  }

  const handleSaveWorker = dialogForm.handleSubmit(async (data) => {
    // GW-154 pre-flight: block shortening an existing worker's Last Day of Work past submitted hours or live
    // transactions, so the invalid date never reaches the engagement doc (a failed sync would leave it
    // persisted while the work order was never wound down). Runs on an actual *reduction* (matching the backend
    // guard, which fires only when newEnd < priorEnd) against a persisted engagement; prior effective end
    // mirrors the backend's `LastDayOfWork ?? CoreInfo.EndDate`. No client-side "is this worker synced" gate —
    // the scoped endpoint is the authority: a worker with no assignment on this engagement (just added this
    // session, or a brand-new engagement) comes back as a plain no-conflict, so only a genuine failure
    // (5xx/transport) reaches the catch → fail closed with a retryable error rather than saving the date blind.
    const original = workers.find(w => w.workerUserProfileId === data.workerUserProfileId)
    const priorEnd = original?.lastDayOfWork ?? engagementEndDate ?? null
    const isReduction = !!priorEnd && !!data.lastDayOfWork && data.lastDayOfWork < priorEnd
    if (
      data.lastDayOfWork &&
      isReduction &&
      !data.isRemoved &&
      engagementRecordId &&
      data.workerUserProfileId
    ) {
      setIsCheckingEndDate(true)
      try {
        const result = await checkEndDateConflict(engagementRecordId, data.workerUserProfileId, formatDateOnly(data.lastDayOfWork))
        if (result.hasConflict) {
          const message = result.hasSubmittedHoursConflict
            ? (dictionary?.engagement?.workers?.lastDayOfWorkHoursConflict || "There are submitted timesheets with hours after this date. Choose a later Last Day of Work.")
            : (dictionary?.engagement?.workers?.lastDayOfWorkTransactionConflict || "There are billing or payment transactions after this date. Choose a later Last Day of Work.")
          dialogForm.setError('lastDayOfWork', { type: 'manual', message })
          return
        }
      } catch (error) {
        console.error('Last Day of Work conflict pre-check failed:', error)
        dialogForm.setError('lastDayOfWork', {
          type: 'manual',
          message: dictionary?.engagement?.workers?.lastDayOfWorkCheckFailed || "Unable to validate this date right now. Please try again.",
        })
        return
      } finally {
        setIsCheckingEndDate(false)
      }
    }

    // GW-154 pre-flight: block pushing an existing worker's First Day of Work later than submitted hours or
    // live transactions already recorded before it (mirror of the Last Day of Work check above). Prior
    // effective start mirrors the backend's `FirstDayOfWork ?? CoreInfo.StartDate`. Runs on an actual forward
    // *push* against a persisted engagement; the endpoint is the authority (a non-member worker returns a plain
    // no-conflict), so only genuine failures reach the catch → fail closed. Same rationale as LDoW above.
    const priorStart = original?.firstDayOfWork ?? engagementStartDate ?? null
    const isStartPush = !!priorStart && !!data.firstDayOfWork && data.firstDayOfWork > priorStart
    if (
      data.firstDayOfWork &&
      isStartPush &&
      !data.isRemoved &&
      engagementRecordId &&
      data.workerUserProfileId
    ) {
      setIsCheckingEndDate(true)
      try {
        const result = await checkStartDateConflict(engagementRecordId, data.workerUserProfileId, formatDateOnly(data.firstDayOfWork))
        if (result.hasConflict) {
          const message = result.hasSubmittedHoursConflict
            ? (dictionary?.engagement?.workers?.firstDayOfWorkHoursConflict || "There are submitted timesheets with hours before this date. Choose an earlier First Day of Work.")
            : (dictionary?.engagement?.workers?.firstDayOfWorkTransactionConflict || "There are billing or payment transactions before this date. Choose an earlier First Day of Work.")
          dialogForm.setError('firstDayOfWork', { type: 'manual', message })
          return
        }
      } catch (error) {
        console.error('First Day of Work conflict pre-check failed:', error)
        dialogForm.setError('firstDayOfWork', {
          type: 'manual',
          message: dictionary?.engagement?.workers?.firstDayOfWorkCheckFailed || "Unable to validate this date right now. Please try again.",
        })
        return
      } finally {
        setIsCheckingEndDate(false)
      }
    }

    if (isEditMode && !data.isRemoved) {
      const confirmation = lastDayChangeConfirmation(original, data)
      if (confirmation) {
        setLastDayConfirmation({ ...confirmation, onConfirm: () => commitWorker(data) })
        return
      }
    }

    commitWorker(data)
  })

  const commitWorker = (data: Worker) => {
    if (isEditMode) {
      // Edit existing worker
      onWorkerEdit(data)
    } else {
      // Add new worker
      onWorkerAdd(data)
    }
    closeDialog()
  }

  const workerText = dictionary?.engagement?.workers
  const countedMessage = (one: string, many: string, count: number) =>
    count === 1 ? one : many.replace('{count}', String(count))

  /**
   * What the reader should know before a moved Last Day of Work is saved, when it affects the worker's
   * pending hours changes. Moving it earlier leaves later changes waiting; moving it later brings back any
   * that were waiting and are dated today or earlier, which then apply on the next nightly run.
   */
  const lastDayChangeConfirmation = (
    original: Worker | undefined,
    data: Worker
  ): Omit<ScheduledHoursConfirmation, 'onConfirm'> | null => {
    const { notTakingEffect, nowDue } = lastDayOfWorkEffects(
      data.scheduledBudgetedHours,
      effectiveLastDay(original?.lastDayOfWork, engagementEndDate),
      effectiveLastDay(data.lastDayOfWork, engagementEndDate),
      todayDateOnly()
    )
    const confirmLabel = workerText?.save || "Save"

    if (notTakingEffect.length > 0) {
      return {
        title: workerText?.changesAfterLastDayTitle || "Scheduled hours changes after the last day",
        message: countedMessage(
          workerText?.changesAfterLastDayOne || "1 scheduled change to budgeted hours per day falls after this Last Day of Work. It won't take effect unless the Last Day of Work is extended.",
          workerText?.changesAfterLastDayMany || "{count} scheduled changes to budgeted hours per day fall after this Last Day of Work. They won't take effect unless the Last Day of Work is extended.",
          notTakingEffect.length
        ),
        confirmLabel,
      }
    }

    if (nowDue.length > 0) {
      const latest = nowDue[nowDue.length - 1]
      return {
        title: workerText?.changesNowDueTitle || "Scheduled hours changes now due",
        message: countedMessage(
          workerText?.changesNowDueOne || "1 scheduled change to budgeted hours per day is dated today or earlier and will apply tonight. It sets the hours to {hours} from {date}. Cancel it before saving if it is no longer wanted.",
          workerText?.changesNowDueMany || "{count} scheduled changes to budgeted hours per day are dated today or earlier and will apply tonight. The latest sets the hours to {hours} from {date}. Cancel any you no longer want before saving.",
          nowDue.length
        )
          .replace('{hours}', String(latest.hoursPerDay))
          .replace('{date}', formatDate(parseDateOnly(latest.effectiveDate))),
        confirmLabel,
      }
    }

    return null
  }

  const removeWorker = (workerUserProfileId: number, shouldSoftRemove: boolean) => {    
    if (shouldSoftRemove) {
      const worker = workers.find(w => w.workerUserProfileId === workerUserProfileId)

      if (!worker) {
        console.error(`Worker ${workerUserProfileId} not found`)
        return
      }

      // Soft remove - show date dialog for last day of work
      setPendingWorkerRemoval(worker)
      setIsDateDialogOpen(true)
    } else {
      // Hard remove - actually remove from array
      onWorkerRemove(workerUserProfileId)
    }
  }

  const restoreWorker = async (workerUserProfileId: number) => {
    // Toggle the isRemoved flag back to false and clear lastDayOfWork. Also clear any termination
    // metadata carried over from a server-loaded terminated worker — once restored the worker is
    // active again, so leaving stale termination type/reason would re-flag them as Terminated on a
    // later plain removal (and round-trip that termination back to the backend on save).
    const worker = workers.find(w => w.workerUserProfileId === workerUserProfileId)
    if (!worker) return

    // Re-derive the pay shape from the worker's CURRENT profile rather than reusing the stored shape.
    // A worker can be removed, converted (e.g. T4 Hourly -> T4 Salaried) while off the engagement, then
    // restored; reusing the old shape would re-add a salaried worker with a stale hourly pay block and
    // recreate the illegal hourly-WO-on-a-salaried-worker split (GW-210). Uses the same profile mappers
    // as the add flow. If the profile can't be resolved (or the fetch fails) we fall back to the stored
    // shape so restore still works; an actually-corrupt salaried profile surfaces null reportingPayRate,
    // which the worker form schema then blocks on save.
    let payShape: Partial<Worker> = {}
    try {
      const profile = workerUserProfiles.find(p => p.Id === workerUserProfileId)
      if (profile) {
        if (getWorkerTypeFromProfile(profile) === WORKER_TYPE.T4_SALARIED) {
          const t4Details = await fetchT4SalariedDetails(workerUserProfileId)
          payShape = salariedPayShapeFromDetails(t4Details.T4SalariedDetails)
        } else {
          const engagementDetails = await fetchEngagementDetails(workerUserProfileId)
          // Salaried when removed means no hourly rates to bring back, so they start like a new worker.
          const heldRates = worker.type === WORKER_TYPE.T4_SALARIED ? [] : worker.payRates
          payShape = hourlyPayShapeFromDetails(engagementDetails?.EngagementDetails, seedCoverage, heldRates)
        }
      }
    } catch (error) {
      console.error('Failed to re-pull profile pay shape on restore:', error)
    }

    onWorkerEdit({
      ...worker,
      ...payShape,
      // The profile mappers know nothing about the engagement, so the rates they hand back arrive
      // without a billability and default to billable on the way out. Held to what the engagement
      // allows here, as the dialog does, or restoring onto a milestone or internal one is refused.
      payRates: payShape.payRates ?? worker.payRates,
      // Hours belong to the engagement, where applied scheduled changes have moved them off the profile's.
      budgetedHours: worker.budgetedHours ?? payShape.budgetedHours ?? null,
      isRemoved: false,
      lastDayOfWork: null,
      terminationTypeId: null,
      terminationReasonIds: null,
    })
  }

  const handleDateConfirmation = (date: Date) => {
    if (!pendingWorkerRemoval) return

    // Saving the removal cancels every pending hours change, whatever its date, so ask first. Go Back
    // returns to the date, with the worker still pending removal.
    const pendingChanges = pendingEntries(pendingWorkerRemoval.scheduledBudgetedHours).length
    if (pendingChanges > 0) {
      setRemovalConfirmation({
        title: workerText?.removalCancelsChangesTitle || "Cancel scheduled hours changes?",
        message: countedMessage(
          workerText?.removalCancelsChangesOne || "This worker has a scheduled change to budgeted hours per day. Removing the worker cancels it.",
          workerText?.removalCancelsChangesMany || "This worker has {count} scheduled changes to budgeted hours per day. Removing the worker cancels them.",
          pendingChanges
        ),
        confirmLabel: workerText?.removeWorker || "Remove Worker",
        onConfirm: () => removeWithLastDay(date),
        onBack: () => setIsDateDialogOpen(true),
      })
      return
    }

    removeWithLastDay(date)
  }

  const removeWithLastDay = (date: Date) => {
    if (!pendingWorkerRemoval) return

    // Remove worker with last day of work. A removal initiated from this UI is always a plain
    // "Removed" (this flow never originates a termination), so clear any stale termination metadata
    // that may have been carried over from a previously-restored terminated worker — otherwise the
    // worker would be mislabeled and saved as Terminated.
    const workerWithDate = {
      ...pendingWorkerRemoval,
      isRemoved: true,
      lastDayOfWork: date,
      terminationTypeId: null,
      terminationReasonIds: null
    }
    onWorkerEdit(workerWithDate)

    // Clear pending removal
    setPendingWorkerRemoval(null)
  }

  return (
    <>
      <FormSectionCard
        id="workers"
        title={dictionary?.engagement?.workers?.title || "Workers"}
        description={dictionary?.engagement?.workers?.description || "Manage workers assigned to the engagement"}
        icon={Users}
        isCompleted={isCompleted}
        showPrevNext={true}
        {...navigationProps}
      >
        <div className="flex justify-end items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            className="gap-1"
            onClick={() => navigateToParent("/next/contact/wizardworkerprofile", { openInNewTab: true })}
          >
            Create new Worker
            <ExternalLink className="h-3 w-3" />
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                size="sm" 
                className="gap-1" 
                onClick={openAddDialog}
                disabled={isReadonly || allWorkersSelected}
                title={allWorkersSelected ? "All available workers are already selected" : undefined}
              >
                <PlusCircle className="h-4 w-4" />
                {dictionary?.engagement?.workers?.addWorker || "Add Worker"}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[550px]">
              <DialogHeader>
                <DialogTitle>
                  {isEditMode 
                    ? (dictionary?.engagement?.workers?.editWorker || "Edit Worker")
                    : (dictionary?.engagement?.workers?.addWorker || "Add Worker")
                  }
                </DialogTitle>
                <DialogDescription>
                  {isEditMode
                    ? (dictionary?.engagement?.workers?.editWorkerDescription || "Update the worker details")
                    : (dictionary?.engagement?.workers?.addWorkerDescription || "Add an existing worker to the engagement")
                  }
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col max-h-[calc(90vh-140px)] -mx-1">
                <ScrollArea className="flex-1 overflow-y-auto px-1">
                  <Form {...dialogForm}>
                    {/* An existing worker opens on what the card already flagged, so its errors show at
                        once. A worker being added starts blank and is told only once they act. */}
                    <FormErrorsVisible value={isEditMode}>
                    <div className="space-y-5 py-4">
                    {dialogHeadSaveErrors.map(message => (
                      <p key={message} className="text-sm text-red-700">{message}</p>
                    ))}
                    <FormField
                      control={dialogForm.control}
                      name="workerUserProfileId"
                      render={({ field }) => (
                        <FormItem className="space-y-2.5">
                          <FormLabel className="text-sm font-medium">
                            {dictionary?.engagement?.workers?.workerName || "Worker Name"}
                          </FormLabel>
                          <div className="flex gap-2 items-end">
                            <div className="flex-1">
                              <FormControl>
                                <VirtualizedSelect
                                  {...field}
                                  className="form-field-focus"
                                  options={workerOptions}
                                  value={field.value?.toString() || ""}
                                  getItemValue={(item) => item.profile.Id.toString()}
                                  getItemLabel={(item) => {
                                    const lob = getLineOfBusinessText(item.profile)
                                    return `${item.profile.DisplayValue} [${getProfileTypeText(item.profile)}]${lob ? ` [${lob}]` : ''}`
                                  }}
                                  onValueChange={async (value) => {
                                    const profileId = parseInt(value)
                                    field.onChange(profileId)
                                    await handleWorkerSelection(profileId)
                                  }}
                                  placeholder={dictionary?.engagement?.workers?.selectWorker || "Select worker"}
                                  searchPlaceholder="Search workers..."
                                  emptyMessage={loadingWorkerProfiles ? "Loading workers..." : undefined}
                                  disabled={isEditMode}
                                />
                              </FormControl>
                            </div>
                            {!isEditMode && (
                              <Button 
                                variant="outline" 
                                className="px-3"
                                onClick={() => navigateToParent("/next/contact/wizardworkerprofile", { openInNewTab: true })}
                                title="Create New Worker"
                              >
                                <UserPlus className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={dialogForm.control}
                        name="jobCategoryCode"
                        render={({ field }) => (
                          <FormItem className="space-y-2.5">
                            <FormLabel className="text-sm font-medium">
                              {dictionary?.engagement?.workers?.jobCategory || "Job Category"}
                            </FormLabel>
                            <FormControl>
                              <Select
                                {...field}
                                value={field.value || ""}
                                onValueChange={(value) => {
                                  field.onChange(value)
                                  dialogForm.setValue('jobFunctionCode', '')
                                }}
                                allowClear
                              >
                                <SelectTrigger className="form-field-focus">
                                  <SelectValue
                                    placeholder={dictionary?.engagement?.workers?.selectJobCategory || "Select job category"}
                                  />
                                </SelectTrigger>
                                <SelectContent>
                                  {getByCategory('workorder.CodeJobCategory').map(category => (
                                    <SelectItem key={category.code} value={category.code!}>
                                      {category.text}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={dialogForm.control}
                        name="jobFunctionCode"
                        render={({ field }) => (
                          <FormItem className="space-y-2.5">
                            <FormLabel className="text-sm font-medium">
                              {dictionary?.engagement?.workers?.jobFunction || "Job Function"}
                            </FormLabel>
                            <FormControl>
                              <Select
                                {...field}
                                value={field.value || ""}
                                onValueChange={field.onChange}
                                disabled={!dialogWorker.jobCategoryCode}
                                allowClear
                              >
                                <SelectTrigger className="form-field-focus">
                                  <SelectValue
                                    placeholder={!dialogWorker.jobCategoryCode 
                                      ? (dictionary?.engagement?.workers?.selectJobCategoryFirst || "Select job category first")
                                      : (dictionary?.engagement?.workers?.selectJobFunction || "Select job function")
                                    }
                                  />
                                </SelectTrigger>
                                <SelectContent>
                                  {dialogWorker.jobCategoryCode && 
                                    getRelatedCodeValuesByCode('workorder.CodeJobFunction', dialogWorker.jobCategoryCode, 'workorder.CodeJobCategory')
                                      .map(jobFunction => (
                                        <SelectItem key={jobFunction.code} value={jobFunction.code!}>
                                          {jobFunction.text}
                                        </SelectItem>
                                      ))
                                  }
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={dialogForm.control}
                      name="jobTitle"
                      render={({ field }) => (
                        <FormItem className="space-y-2.5">
                          <FormLabel className="text-sm font-medium">
                            {dictionary?.engagement?.workers?.jobTitle || "Job Title"}
                          </FormLabel>
                          <FormControl>
                            <DebouncedInput
                              {...field}
                              placeholder={dictionary?.engagement?.workers?.jobTitlePlaceholder || "Enter job title"}
                              className="form-field-focus"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {dialogWorker.lineOfBusinessCode && (
                      <FormField
                        control={dialogForm.control}
                        name="lineOfBusinessCode"
                        render={({ field }) => {
                          const lobCodeValues = getByCategory('org.CodeLineOfBusiness')
                          const matchedLob = lobCodeValues.find(lob => lob.code === field.value)
                          
                          return (
                            <FormItem className="space-y-2.5">
                              <FormLabel className="text-sm font-medium">
                                {dictionary?.engagement?.workers?.lineOfBusiness || "Line of Business"}
                              </FormLabel>
                              <FormControl>
                                <Input
                                  value={field.value ? (matchedLob?.description || matchedLob?.text || field.value) : "-"}
                                  disabled={true}
                                  className="form-field-focus"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )
                        }}
                      />
                    )}

                    <FormField
                      control={dialogForm.control}
                      name="budgetedHours"
                      render={({ field }) => (
                        <FormItem className="space-y-2.5">
                          <FormLabel className="text-sm font-medium">
                            {dictionary?.engagement?.workers?.budgetedHoursPerDay || "Budgeted Hours per Day"}
                          </FormLabel>
                          <FormControl>
                            <DecimalInput
                              {...field}
                              decimalPlaces={2}
                              minDecimalPlaces={0}
                              className="form-field-focus"
                              placeholder="Enter hours"
                            />
                          </FormControl>
                          <FormMessage />
                          {!dialogWorker.isRemoved && (
                            <div className="space-y-1">
                              <ScheduledHoursSummary
                                entries={dialogWorker.scheduledBudgetedHours}
                                lastDay={dialogScheduleBounds.lastDay}
                              />
                              {dialogScheduleErrors.map(message => (
                                <p key={message} className="text-sm font-medium text-destructive">{message}</p>
                              ))}
                              {!isReadonly && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setIsScheduleDialogOpen(true)}
                                >
                                  {workerText?.scheduleChange || "Schedule Change"}
                                </Button>
                              )}
                            </div>
                          )}
                        </FormItem>
                      )}
                    />

                    <ScheduleBudgetedHoursDialog
                      open={isScheduleDialogOpen}
                      onOpenChange={setIsScheduleDialogOpen}
                      entries={dialogWorker.scheduledBudgetedHours ?? []}
                      currentHours={dialogWorker.budgetedHours}
                      bounds={dialogScheduleBounds}
                      onEntriesChange={entries =>
                        dialogForm.setValue('scheduledBudgetedHours', entries, { shouldDirty: true, shouldValidate: true })}
                    />
                    <ScheduledHoursConfirmDialog
                      confirmation={lastDayConfirmation}
                      onClose={() => setLastDayConfirmation(null)}
                    />

                    {/* Engagement Start Date - always visible and required */}
                    <FormField
                      control={dialogForm.control}
                      name="firstDayOfWork"
                      render={({ field }) => (
                        <FormItem className="space-y-2.5">
                          <FormLabel className="text-sm font-medium">
                            {dictionary?.engagement?.workers?.engagementStartDate || "Engagement Start Date"}
                          </FormLabel>
                          <FormControl>
                            <DatePicker
                              {...field}
                              placeholder={dictionary?.engagement?.core?.pickDate || "Pick a date"}
                              minDate={engagementStartDate || undefined}
                              maxDate={engagementEndDate || undefined}
                              allowClear={false}
                              allowTextInput
                            />
                          </FormControl>
                          <FormMessage />
                          {dialogSaveErrorUnder('firstDayOfWork', Boolean(dialogForm.formState.errors.firstDayOfWork)) && (
                            <p className="text-sm font-medium text-destructive">{dialogSaveErrors.firstDayOfWork}</p>
                          )}
                        </FormItem>
                      )}
                    />

                    {/* Last Day of Work — per-worker end date (active: winds down early; removed: termination date) */}
                    <FormField
                      control={dialogForm.control}
                      name="lastDayOfWork"
                      render={({ field }) => (
                        <FormItem className="space-y-2.5">
                          <FormLabel className="text-sm font-medium">
                            {dictionary?.engagement?.workers?.lastDayOfWork || "Last Day of Work"}
                          </FormLabel>
                          <FormControl>
                            <DatePicker
                              {...field}
                              placeholder={dictionary?.engagement?.core?.pickDate || "Pick a date"}
                              minDate={dialogWorker.firstDayOfWork || engagementStartDate || undefined}
                              maxDate={engagementEndDate || undefined}
                              allowClear={true}
                              allowTextInput
                            />
                          </FormControl>
                          {!dialogWorker.isRemoved && (
                            <p className="text-xs text-muted-foreground">
                              {dictionary?.engagement?.workers?.lastDayOfWorkHint || "Leave blank to align with the engagement's end date and any future changes to it."}
                            </p>
                          )}
                          <FormMessage />
                          {dialogSaveErrorUnder('lastDayOfWork', Boolean(dialogForm.formState.errors.lastDayOfWork)) && (
                            <p className="text-sm font-medium text-destructive">{dialogSaveErrors.lastDayOfWork}</p>
                          )}
                        </FormItem>
                      )}
                    />

                    {billsForHours && (
                      <div className="flex gap-2">
                        <FormField
                          control={dialogForm.control}
                          name="billRate"
                          render={({ field }) => (
                            <FormItem className="flex-1 space-y-2.5">
                              <FormLabel className="text-sm font-medium">
                                {dictionary?.engagement?.workers?.billRate || "Bill Rate"}
                              </FormLabel>
                              <FormControl>
                                <CurrencyInput
                                  {...field}
                                  currency={currency}
                                  className="form-field-focus"
                                  placeholder="Enter bill rate"
                                />
                              </FormControl>
                              <FormMessage />
                              {dialogBillRateWarning && (
                                <p className="text-sm font-medium text-amber-700">{dialogBillRateWarning}</p>
                              )}
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={dialogForm.control}
                          name="billRateUnit"
                          render={({ field }) => (
                            <FormItem className="w-[140px] space-y-2.5">
                              <FormLabel className="text-sm font-medium text-transparent">
                                {dictionary?.engagement?.workers?.unit || "Unit"}
                              </FormLabel>
                              <FormControl>
                                <Select
                                  {...field}
                                  value={field.value || RATE_UNIT.HOUR}
                                  onValueChange={field.onChange}
                                  disabled={true}
                                >
                                  <SelectTrigger className="form-field-focus" disabled={true}>
                                    <SelectValue placeholder={dictionary?.engagement?.workers?.unit || "Unit"} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {getByCategory('workorder.CodeRateUnit')
                                      .filter(unit => unit.code === RATE_UNIT.HOUR)
                                      .map(unit => (
                                        <SelectItem key={unit.code} value={unit.code!}>
                                          {unit.text}
                                        </SelectItem>
                                      ))}
                                  </SelectContent>
                                </Select>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    )}

                    {/* Reporting Bill Rate - only for Milestone billing */}
                    {billingType === ENGAGEMENT_BILLING_TYPE.MILESTONE && (
                      <FormField
                        control={dialogForm.control}
                        name="reportingBillRate"
                        render={({ field }) => (
                          <FormItem className="space-y-2.5">
                            <FormLabel className="text-sm font-medium">
                              {dictionary?.engagement?.workers?.reportingBillRate || "Reporting Bill Rate"}
                            </FormLabel>
                            <FormControl>
                              <CurrencyInput
                                {...field}
                                currency={currency}
                                placeholder="Enter reported bill rate"
                                className="form-field-focus"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    {dialogWorker.type === WORKER_TYPE.REGULAR && (
                      <>
                        {canViewPayRates && (
                          <>
                        {/* Every rate the worker is paid at here. The amount, the unit and the name come
                            from the profile and are read-only, exactly as the single pay rate always was:
                            the engagement selects which rates apply, it never authors one. */}
                        <div className="space-y-2.5">
                          <div className="flex gap-2">
                            <FormLabel className="flex-1 text-sm font-medium">
                              {dialogPayRates.length > 1
                                ? dictionary?.engagement?.workers?.payRates || "Pay Rates"
                                : dictionary?.engagement?.workers?.payRate || "Pay Rate"}
                            </FormLabel>
                            {/* Holds the label clear of the columns beside each rate: the unit, the
                                billability where it is shown, and the remove button, plus the gaps. */}
                            <span className={billsForHours ? "w-[346px]" : "w-[188px]"} />
                          </div>
                          {dialogPayRates.map((rate, index) => (
                            <div key={rate.rateTypeCode || index} className="space-y-1">
                              {/* The rate type names the rate, and the description is whatever wording the
                                  profile added beside it. Labelled whether the worker holds one rate or
                                  several: an unlabelled amount leaves the reader guessing which of the
                                  profile's rates they are looking at, and which one a second would sit
                                  beside. */}
                              <p className="text-xs text-muted-foreground">
                                {payRateLabel(rate.rateTypeCode, rate.description)}
                              </p>
                              {/* Every field here is read-only, so the only thing that can be wrong with a
                                  rate is an amount the profile never set. The message names the rate, so a
                                  worker with several knows which one to go and fix. */}
                              {payRateErrorFor(index) && (
                                <p className="text-xs text-amber-600">{payRateErrorFor(index)}</p>
                              )}
                              <div className="flex items-center gap-2">
                                <CurrencyInput
                                  value={rate.payRate ?? null}
                                  onChange={() => undefined}
                                  currency={dialogWorker.paymentCurrencyCode || currency}
                                  className="flex-1 form-field-focus"
                                  placeholder="Enter pay rate"
                                  disabled={true}
                                />
                                <Select value={rate.payRateUnitCode || RATE_UNIT.HOUR} disabled={true}>
                                  <SelectTrigger className="w-[140px] form-field-focus" disabled={true}>
                                    <SelectValue placeholder={dictionary?.engagement?.workers?.unit || "Unit"} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {getByCategory('workorder.CodeRateUnit').map(unit => (
                                      <SelectItem key={unit.code} value={unit.code!}>
                                        {unit.text}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {/* Whether hours at this rate are invoiced, read from the rate itself.
                                    Shown rather than offered: it comes with the rate, and it is what says
                                    which of an engagement's tasks the hours can be recorded against.
                                    Left out where nothing here is billed, the same way the Bill Rate
                                    field is: the rate still says what it says on the profile, and this
                                    engagement is not the place that reads it. */}
                                {billsForHours && (
                                  <div className="w-[150px] shrink-0 text-sm text-muted-foreground">
                                    {rate.billable === false
                                      ? (dictionary?.engagement?.workers?.nonBillable || "Non-billable")
                                      : (dictionary?.engagement?.workers?.billable || "Billable")}
                                  </div>
                                )}
                                {/* Every rate but Primary can be taken off. Every placement holds Primary,
                                    so it has no remove control rather than one the save would refuse. */}
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center">
                                  {!isReadonly && !isBaseRateType(rate.rateTypeCode) && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => removePayRate(index)}
                                      aria-label={dictionary?.engagement?.workers?.removeRate || "Remove rate"}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                          {/* Adding a rate is the engagement's one decision about it. The amount, the unit
                              and the name all come from the profile, so there is nothing else to fill in. */}
                          {!isReadonly && availablePayRates.length > 0 && (
                            <Select value="" onValueChange={addPayRate}>
                              <SelectTrigger className="form-field-focus">
                                <SelectValue placeholder={dictionary?.engagement?.workers?.addRate || "Add a rate from the worker profile"} />
                              </SelectTrigger>
                              <SelectContent>
                                {availablePayRates.map((rate, index) => (
                                  <SelectItem key={rate.Id || index} value={rate.Id || ''}>
                                    {payRateLabel(rate.RateTypeCode, rate.Description)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          {/* Every rate the worker's profile holds is already on this engagement. */}
                          {!isReadonly && availablePayRates.length === 0 && dialogPayRates.length > 0 && (
                            <p className="text-xs text-muted-foreground">
                              {dictionary?.engagement?.workers?.noRatesLeftToAdd
                                || "Every rate on this worker's profile is already on this engagement."}
                            </p>
                          )}
                          {/* What is wrong with the list as a whole rather than with one rate in it:
                              holding none, or holding two of a kind. Read off the array itself, which is
                              where the schema reports both and where nothing was rendering them. */}
                          {payRateListError && (
                            <p className="text-xs text-destructive">{payRateListError}</p>
                          )}
                          {dialogSaveErrorUnder('payRates', Boolean(dialogForm.formState.errors.payRates)) && (
                            <p className="text-xs text-destructive">{dialogSaveErrors.payRates}</p>
                          )}
                        </div>
                          </>
                        )}

                        <FormField
                          control={dialogForm.control}
                          name="paymentCurrencyCode"
                          render={({ field }) => (
                            <FormItem className="space-y-2.5">
                              <FormLabel className="text-sm font-medium">
                                {dictionary?.engagement?.workers?.paymentCurrency || "Payment Currency"}
                              </FormLabel>
                              <FormControl>
                                <Select
                                  {...field}
                                  value={field.value || CURRENCY_OVERRIDE_NONE}
                                  onValueChange={(val) => field.onChange(val === CURRENCY_OVERRIDE_NONE ? null : val)}
                                >
                                  <SelectTrigger className="form-field-focus">
                                    <SelectValue placeholder={dictionary?.engagement?.workers?.selectPaymentCurrency || "Select payment currency"} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value={CURRENCY_OVERRIDE_NONE}>
                                      {dictionary?.engagement?.workers?.useEngagementCurrency || "Use engagement currency"}
                                    </SelectItem>
                                    {getByCategory('geo.CodeCurrency').map(currencyItem => (
                                      <SelectItem key={currencyItem.code} value={currencyItem.code!}>
                                        {currencyItem.text}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={dialogForm.control}
                          name="paySchedule"
                          render={({ field }) => (
                            <FormItem className="space-y-2.5">
                              <FormLabel className="text-sm font-medium">
                                {dictionary?.engagement?.workers?.paySchedule || "Pay Schedule"}
                              </FormLabel>
                              <FormControl>
                                <Select
                                  {...field}
                                  value={field.value || ""}
                                  onValueChange={field.onChange}
                                >
                                  <SelectTrigger className="form-field-focus">
                                    <SelectValue
                                      placeholder={
                                        dictionary?.engagement?.workers?.selectPaySchedule || "Select pay schedule"
                                      }
                                    />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {getByCategory('workorder.CodePaymentReleaseSchedule').map(schedule => (
                                      <SelectItem key={schedule.code} value={schedule.code!}>
                                        {schedule.text}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </FormControl>
                              <FormMessage />
                              {/* GW-237: the pay schedule is now set per engagement (it defaults from the profile on
                                  add but can be overridden). Warn — but don't block — when an already-persisted
                                  worker's schedule is changed: it retimes the planned release date / pay period for
                                  FUTURE payments (payments already processed keep their original schedule). Mirrors
                                  the traditional work order's in-place correction, which prompts rather than blocks. */}
                              {editingWorkerOriginal &&
                                (field.value || "") !== (editingWorkerOriginal.paySchedule || "") && (
                                  <p className="text-xs text-amber-600">
                                    {dictionary?.engagement?.workers?.payScheduleChangeWarning ||
                                      "Changes to pay schedule will impact future payments. Existing transactions will not be changed."}
                                  </p>
                                )}
                            </FormItem>
                          )}
                        />
                      </>
                    )}

                    {canViewPayRates && dialogWorker.type === WORKER_TYPE.T4_SALARIED && (
                      <FormField
                        control={dialogForm.control}
                        name="reportingPayRate"
                        render={({ field }) => (
                          <FormItem className="space-y-2.5">
                            <FormLabel className="text-sm font-medium">
                              {dictionary?.engagement?.workers?.reportingPayRate || "Reporting Pay Rate"}
                            </FormLabel>
                            <FormControl>
                              <CurrencyInput
                                {...field}
                                currency={dialogWorker.paymentCurrencyCode || currency}
                                className="form-field-focus"
                                placeholder="Enter reporting pay rate"
                                disabled={true}
                              />
                            </FormControl>
                            <FormMessage />
                            {!field.value && dialogWorker.workerUserProfileId > 0 && (
                              <p className="text-xs text-amber-600">
                                {dictionary?.engagement?.workers?.profileFieldRequired || "This field must be set on the worker profile. Please complete the profile before continuing."}
                              </p>
                            )}
                          </FormItem>
                        )}
                      />
                    )}
                  </div>
                    </FormErrorsVisible>
                  </Form>
                </ScrollArea>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeDialog}>
                  {dictionary?.engagement?.core?.cancel || "Cancel"}
                </Button>
                <Button onClick={handleSaveWorker} disabled={isCheckingEndDate}>
                  {isCheckingEndDate
                    ? (dictionary?.engagement?.core?.checking || "Checking…")
                    : isEditMode
                      ? (dictionary?.engagement?.workers?.updateWorker || "Update Worker")
                      : (dictionary?.engagement?.workers?.addWorker || "Add Worker")
                  }
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Last Day Dialog for Worker Removal */}
        <WorkerRemovalDateDialog
          open={isDateDialogOpen}
          onOpenChange={setIsDateDialogOpen}
          worker={pendingWorkerRemoval || undefined}
          onConfirm={handleDateConfirmation}
          engagementStartDate={engagementStartDate}
          engagementEndDate={engagementEndDate}
        />
        <ScheduledHoursConfirmDialog
          confirmation={removalConfirmation}
          onClose={() => setRemovalConfirmation(null)}
        />
        {workers.length > 0 ? (
          <div className="space-y-4">
            {/* Show form-level errors when workers exist but validation fails */}
            {form.formState.errors.workers && typeof form.formState.errors.workers === 'object' && 'message' in form.formState.errors.workers && (
              <div className="border border-destructive/20 rounded-md p-4 bg-destructive/5">
                <div className="flex items-center gap-2 text-destructive">
                  <span className="text-sm font-medium">
                    {form.formState.errors.workers.message}
                  </span>
                </div>
              </div>
            )}
            
            {workers.map((worker, index) => (
              <WorkerCard
                key={worker.workerUserProfileId}
                worker={worker}
                index={index}
                hasError={Boolean(form.formState.errors.workers?.[index])}
                fieldErrors={form.formState.errors.workers?.[index]}
                saveErrors={standingRefusals(workerSaveErrors?.[worker.workerUserProfileId], worker, refusalContext)}
                engagementStatus={engagementStatus}
                engagementEndDate={engagementEndDate}
                isReadonly={isReadonly}
                billingType={billingType}
                engagementType={engagementType}
                onEdit={openEditDialog}
                onRemove={removeWorker}
                onRestore={restoreWorker}
                isExisting={isWorkerExisting(worker.workerUserProfileId, previousWorkerIds)}
                currency={currency}
                canViewPayRates={canViewPayRates}
                workerProfiles={workerProfiles}
              />
            ))}
          </div>
        ) : (
          <div className="border rounded-md p-6 text-center text-muted-foreground bg-muted/10">
            {dictionary?.engagement?.workers?.noWorkersAdded || "No workers have been added yet."}
          </div>
        )}
      </FormSectionCard>
    </>
  )
}

export default WorkersSection